import { useState, useCallback } from "react";
import { Button } from "./Button";
import { usePagePermission } from "@/hooks/permission/usePagePermission";
import { TableColumn } from "./Table";
import { printTable, PrintOptions } from "@/lib/print";

interface PrintButtonProps<T> extends PrintOptions {
  /** 출력할 데이터 */
  data: T[];
  /** 테이블 컬럼 정의 */
  columns: TableColumn[];
  /** 버튼 텍스트 (기본: "출력") */
  children?: React.ReactNode;
  /** 권한 체크 비활성화 여부 */
  skipPermissionCheck?: boolean;
  /** 추가 비활성화 조건 */
  disabled?: boolean;
  /** 출력 전 콜백 */
  onBeforePrint?: () => void;
  /** 출력 후 콜백 */
  onAfterPrint?: () => void;
  /** 에러 발생 시 콜백 */
  onError?: (error: Error) => void;
  /** 버튼 클래스 */
  className?: string;
  /** 파일명 (PDF 다운로드용, 현재 미사용) */
  fileName?: string;
}

/**
 * 인쇄 버튼 컴포넌트
 * 브라우저 기본 인쇄 기능을 사용하여 한글 완벽 지원
 *
 * @example
 * // 기본 사용
 * <PrintButton
 *   data={tableData}
 *   columns={columns}
 *   title="매출현황 보고서"
 * />
 *
 * @example
 * // 세로 방향
 * <PrintButton
 *   data={tableData}
 *   columns={columns}
 *   title="거래내역 보고서"
 *   orientation="portrait"
 * />
 */
export function PrintButton<T extends Record<string, any>>({
  data,
  columns,
  children = "출력",
  skipPermissionCheck = false,
  disabled = false,
  title,
  orientation,
  includeGroupHeader,
  onBeforePrint,
  onAfterPrint,
  onError,
  className,
}: PrintButtonProps<T>) {
  const pagePermission = usePagePermission();
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePrint = useCallback(() => {
    try {
      setIsProcessing(true);
      onBeforePrint?.();

      printTable(data, columns, {
        title,
        orientation,
        includeGroupHeader,
      });

      onAfterPrint?.();
    } catch (error) {
      console.error("인쇄 실패:", error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsProcessing(false);
    }
  }, [data, columns, title, orientation, includeGroupHeader, onBeforePrint, onAfterPrint, onError]);

  // 권한 체크
  const hasPermission = skipPermissionCheck || pagePermission.canPrint;
  const isDisabled =
    !hasPermission || pagePermission.isLoading || disabled || isProcessing || data.length === 0;

  return (
    <Button
      variant="etc"
      className={className}
      disabled={isDisabled}
      onClick={handlePrint}
    >
      {isProcessing ? "처리 중..." : children}
    </Button>
  );
}

export default PrintButton;
